import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  BubbleDataPoint,
  Chart,
  ChartData,
  ChartOptions,
  Plugin,
  ScatterDataPoint,
  ScriptableContext,
  TooltipItem,
} from "chart.js";
import { AnyObject } from "chart.js/types/basic";

import { getApplicationBreakpointState, getSelectedYear } from "../../../selectors/application";
import { getMonthlyWeightData, getMonthlyWeightStatus } from "../../../selectors/monthlyWasteWeight";
import { ApplicationBreakpoint } from "../../../types/application";
import { Status } from "../../../types/state";
import {
  CHART_COLORS,
  CHART_FONT_SIZE,
  formatKgValue,
  getWasteColor,
  getWasteLabel,
  prepareSummaryChartData,
} from "../../../utils/charts/getLineChartData";
import { monthsArray, WasteType } from "../../../utils/enums";
import { capitalizeString } from "../../../utils/formatters/capitalizeString";

interface SummaryLineChartProperties {
  data: ChartData<"line", (number | ScatterDataPoint | BubbleDataPoint | null)[], unknown>;
  plugins: Plugin<"line", AnyObject>[];
  options: ChartOptions<"line">;
  chartRef: React.RefObject<Chart<"line">>;
  isLoading: boolean;
}

export const useGetSummaryLineChartProperties = (): SummaryLineChartProperties => {
  const { t } = useTranslation();
  const selectedYear = useSelector(getSelectedYear);
  const applicationBreakpoint = useSelector(getApplicationBreakpointState);
  const monthlyWeightData = useSelector(getMonthlyWeightData);
  const monthlyWeightStatus = useSelector(getMonthlyWeightStatus);

  const isLoading = monthlyWeightStatus === Status.requesting;

  const chartRef = useRef<Chart<"line">>(null);

  const translatedLabels = monthsArray.map((month) => capitalizeString(t(`enums.months.${month}`)));
  const labels = translatedLabels.map((label) => label.slice(0, 3));

  const scalesFontSize =
    applicationBreakpoint === ApplicationBreakpoint.largeDesktop ||
    applicationBreakpoint === ApplicationBreakpoint.extraLargeDesktop
      ? CHART_FONT_SIZE.desktop
      : CHART_FONT_SIZE.default;

  const { degreesOfSeparation, wasteTypes, monthlyDataByWasteType, maxMonthlyWeight } =
    prepareSummaryChartData(monthlyWeightData);

  const wasteTypeDatasets = wasteTypes.map((wasteType: WasteType) => {
    const color = getWasteColor(wasteType);
    return {
      label: getWasteLabel(wasteType, t),
      data: monthlyDataByWasteType.get(wasteType) ?? Array(12).fill(0),
      fill: false,
      borderColor: color,
      backgroundColor: color,
      yAxisID: "y2",
      borderWidth: 2,
      pointRadius: 0,
      pointHoverRadius: 6,
      pointHitRadius: 12,
      pointBackgroundColor: color,
      pointBorderColor: color,
      order: 1,
    };
  });

  const data: ChartData<"line", (number | ScatterDataPoint | BubbleDataPoint | null)[], unknown> = {
    labels,
    datasets: [
      {
        label: t("degreeOfSeparation"),
        data: degreesOfSeparation,
        fill: true,
        borderColor: CHART_COLORS.separation,
        yAxisID: "y",
        order: 0,
        backgroundColor: (context: ScriptableContext<"line">) => {
          const gradient = context.chart.ctx.createLinearGradient(0, 0, 0, context.chart.height);
          gradient.addColorStop(0, CHART_COLORS.separationGradient.from);
          gradient.addColorStop(0.8, CHART_COLORS.separationGradient.to);
          return gradient;
        },
        borderWidth: 1.5,
        pointRadius: 0,
        pointHoverRadius: 6,
        pointHitRadius: 12,
        pointHoverBorderColor: CHART_COLORS.separation,
        pointBackgroundColor: CHART_COLORS.separation,
        pointBorderColor: CHART_COLORS.separation,
        pointHoverBorderWidth: 2,
      },
      ...wasteTypeDatasets,
    ],
  };

  const options: ChartOptions<"line"> = {
    maintainAspectRatio: false,
    responsive: true,
    interaction: { intersect: false, mode: "index", axis: "x" },
    animation: { duration: 0 },
    elements: { point: { radius: 0, pointStyle: "circle" }, line: { tension: 0.6 } },
    scales: {
      y: {
        beginAtZero: true,
        min: 0,
        suggestedMax: 70,
        position: "left",
        ticks: {
          stepSize: 20,
          callback: (value: string | number) => `${value} ${t("%")}`,
          color: CHART_COLORS.scales,
          font: { size: scalesFontSize, family: "Sora, sans-serif" },
          padding: 15,
        },
      },
      y2: {
        beginAtZero: true,
        position: "right",
        max: maxMonthlyWeight,
        grid: { drawOnChartArea: false },
        ticks: {
          callback: (value: string | number) => formatKgValue(Number(value), t),
          color: CHART_COLORS.scales,
          font: { size: scalesFontSize, family: "Sora, sans-serif" },
          padding: 15,
        },
      },
      x: {
        ticks: {
          color: CHART_COLORS.scales,
          font: { size: scalesFontSize, family: "Sora, sans-serif" },
          padding: 16,
        },
        grid: { display: false },
      },
    },
    plugins: {
      datalabels: { display: false },
      legend: {
        display: true,
        position: "bottom",
        align: "start",
        labels: {
          usePointStyle: true,
          pointStyle: "circle",
          boxWidth: 6,
          boxHeight: 6,
          padding: 20,
          color: CHART_COLORS.legendText,
          font: { size: scalesFontSize - 2, family: "Sora, sans-serif" },
        },
      },
      tooltip: {
        enabled: true,
        backgroundColor: CHART_COLORS.tooltip.background,
        titleColor: CHART_COLORS.tooltip.title,
        bodyColor: CHART_COLORS.tooltip.text,
        borderColor: CHART_COLORS.tooltip.border,
        borderWidth: 1,
        cornerRadius: 12,
        padding: 14,
        boxPadding: 6,
        displayColors: true,
        usePointStyle: true,
        titleFont: {
          size: 13,
          weight: "700",
          family: "Sora, sans-serif",
        },
        bodyFont: {
          size: 13,
          weight: "400",
          family: "Sora, sans-serif",
        },
        callbacks: {
          title: (items: TooltipItem<"line">[]) => {
            const label = translatedLabels.find((l) => l.startsWith(items[0].label)) ?? items[0].label;
            return `${label} ${selectedYear.label}`;
          },
          label: (item: TooltipItem<"line">) =>
            item.dataset.yAxisID === "y"
              ? ` ${item.raw} % ${t("separation").toLowerCase()}`
              : ` ${item.dataset.label}: ${formatKgValue(item.raw as number, t)}`,
        },
      },
    },
  };

  const plugins: Plugin<"line", AnyObject>[] = [
    {
      id: "lineChart",
      afterDraw: (chart: Chart): void => {
        const activeElements = chart.tooltip?.getActiveElements() ?? [];
        if (!activeElements.length) return;
        const { ctx, chartArea } = chart;
        const x = activeElements[0].element.x;
        ctx.save();
        ctx.beginPath();
        ctx.setLineDash([5, 7]);
        ctx.moveTo(x, chartArea.top);
        ctx.lineTo(x, chartArea.bottom);
        ctx.lineWidth = 2;
        ctx.strokeStyle = CHART_COLORS.hoverLine;
        ctx.stroke();
        ctx.restore();
      },
    },
  ];

  return { data, plugins, options, chartRef, isLoading };
};
